import React, { useEffect, useState } from "react";
import Button from "../common/Button";
import logoIcon from "../../assets/images/bytemonk-logo-only.svg";
import logoText from "../../assets/images/bytemonk-text-only.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authCore";
import { resolveAccountWithProvider } from "../../services/authService";
import { auth } from "../../config/firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import UserProfile from "../common/UserProfile";
import { usePageSettings } from "../../context/PageSettingsContext";
import SignInModal from "./navbar/SignInModal";
import ProviderChooserModal from "./navbar/ProviderChooserModal";
import GdgocModal from "./navbar/GdgocModal";
import NavToast from "./navbar/NavToast";
import NavLinks, { NavItem } from "./navbar/NavLinks";
import useNavUnderline from "../../hooks/useNavUnderline";
import { toast } from "../../hooks/useToast";

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signInWithGoogle, signInWithGitHub, isAdmin, isManager } =
    useAuth() as unknown as {
      user?: any;
      signInWithGoogle?: () => Promise<void>;
      signInWithGitHub?: () => Promise<void>;
      isAdmin?: boolean;
      isManager?: boolean;
    };
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showChooserModal, setShowChooserModal] = useState(false);
  const [chooserEmail, setChooserEmail] = useState<string | null>(null);
  const [chooserProviders, setChooserProviders] = useState<string[]>([]);
  const {
    containerRef: desktopNavRef,
    underlineRef: desktopUnderlineRef,
    underlineStyle: desktopUnderlineStyle,
    updateUnderline: updateDesktopUnderline,
  } = useNavUnderline();
  const {
    containerRef: mobileNavRef,
    underlineRef: mobileUnderlineRef,
    underlineStyle: mobileUnderlineStyle,
    updateUnderline: updateMobileUnderline,
  } = useNavUnderline();
  const { isPageEnabled } = usePageSettings();

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.history.replaceState(null, "", "/");
      window.location.reload();
    } else {
      navigate("/");
    }
  };

  const closeSignInModal = () => setShowSignInModal(false);

  const handleGoogleSignIn = async () => {
    try {
      if (!signInWithGoogle) {
        console.warn("Google sign-in not available");
        return;
      }
      await signInWithGoogle();
      closeSignInModal();
    } catch (error) {
      // Detect account-exists with providers info and show chooser
      const e: any = error || {};
      if (e.code === "auth/account-exists-with-different-credential") {
        setChooserEmail(e.email || null);
        setChooserProviders(Array.isArray(e.providers) ? e.providers : []);
        setShowSignInModal(false);
        setShowChooserModal(true);
        return;
      }
      console.error("Google Sign-In Error:", error);
    }
  };

  const handleGitHubSignIn = async () => {
    try {
      if (!signInWithGitHub) {
        console.warn("GitHub sign-in not available");
        return;
      }
      await signInWithGitHub();
      closeSignInModal();
    } catch (error) {
      const e: any = error || {};
      if (e.code === "auth/account-exists-with-different-credential") {
        setChooserEmail(e.email || null);
        setChooserProviders(Array.isArray(e.providers) ? e.providers : []);
        setShowSignInModal(false);
        setShowChooserModal(true);
        return;
      }
      console.error("GitHub Sign-In Error:", error);
    }
  };

  const handleChooserSelect = async (providerId: string) => {
    if (!chooserEmail) return;
    try {
      // For password provider, send reset email
      if (providerId === "password") {
        if (!auth) throw new Error("Auth not initialized");
        await sendPasswordResetEmail(auth as any, chooserEmail);
        toast.info("Password reset email sent if an account exists.");
        setShowChooserModal(false);
        return;
      }

      await resolveAccountWithProvider(chooserEmail, providerId);
      setShowChooserModal(false);
    } catch (err) {
      console.error("Failed to resolve account with provider:", err);
      toast.error("Failed to complete sign-in. Please try again.");
    }
  };

  useEffect(() => {
    updateDesktopUnderline();
    updateMobileUnderline();
  }, [
    location.pathname,
    location.search,
    updateDesktopUnderline,
    updateMobileUnderline,
  ]);

  useEffect(() => {
    if (menuOpen) {
      updateMobileUnderline();
    }
  }, [menuOpen, updateMobileUnderline]);

  const [showGdgocModal, setShowGdgocModal] = useState(false);
  const GDGOC_URL = "https://gdgoc-gcect.web.app/";

  const navItems: NavItem[] = [
    { key: "home", label: "Home", to: "/" },
    {
      key: "events",
      label: "Events",
      to: "/events",
      visible: isPageEnabled("events"),
    },
    {
      key: "karmatek",
      label: "Karmatek",
      to: "/karmatek",
      visible: isPageEnabled("karmatek"),
    },
    {
      key: "gdgoc",
      label: "GDGoC",
      type: "button",
      onClick: () => setShowGdgocModal(true),
    },
    {
      key: "gallery",
      label: "Gallery",
      to: "/gallery",
      visible: isPageEnabled("gallery"),
    },
    { key: "faq", label: "FAQs", to: "/faq", visible: isPageEnabled("faq") },
    {
      key: "about",
      label: "About Us",
      to: "/about-us",
      visible: isPageEnabled("about"),
    },
    {
      key: "admin",
      label: "Admin",
      to: "/admin",
      visible: Boolean(isAdmin || isManager),
    },
    {
      key: "contact",
      label: "Contact Us",
      to: "/contact",
      visible: isPageEnabled("contact"),
      queryParam: "scrollTo=contact",
    },
  ];

  return (
    <>
      <nav
        className="navbar navbar-expand-lg navbar-dark py-3 px-4 mb-5"
        style={{
          position: "fixed",
          top: 0,
          width: "100%",
          zIndex: 10,
          background: "rgba(0, 0, 0, 0.3)",
          backdropFilter: "blur(6px)",
        }}
      >
        <div className="container-fluid d-flex align-items-center justify-content-between">
          {/* Logo */}
          <div
            className="d-flex align-items-center gap-2"
            onClick={handleLogoClick}
            style={{ cursor: "pointer" }}
          >
            <img src={logoIcon} alt="ByteMonk Logo" width={40} height={40} />
            <img
              src={logoText}
              alt="ByteMonk Text"
              width={130}
              height={20}
              className="d-none d-sm-inline"
            />
          </div>

          {/* Center Nav */}
          <NavLinks
            items={navItems}
            locationPathname={location.pathname}
            locationSearch={location.search}
            onNavigate={() => setMenuOpen(false)}
            containerRef={desktopNavRef}
            underlineRef={desktopUnderlineRef}
            underlineStyle={desktopUnderlineStyle}
            className="navbar-nav gap-3 fw-bold d-none d-1200-flex mx-auto position-relative"
            style={{ position: "relative", paddingBottom: "2px" }}
          />

          {/* Right Side */}
          <div className="d-flex align-items-center flex-nowrap gap-2">
            {user ? (
              <div className="d-none d-1200-flex align-items-center gap-2 me-2">
                <UserProfile />
              </div>
            ) : (
              <div className="d-none d-1200-block">
                <Button
                  text="Sign Up"
                  gradient="linear-gradient(135deg, #004e92, #00c6ff)"
                  onClick={() => setShowSignInModal(true)}
                />
              </div>
            )}

            {/* Mobile Right */}
            {user && (
              <div
                className="d-flex d-1200-none align-items-center"
                style={{
                  gap: "0.5rem",
                  whiteSpace: "nowrap",
                  flexWrap: "nowrap",
                }}
              >
                <div
                  className="d-flex align-items-center"
                  style={{ gap: "0.25rem" }}
                >
                  <UserProfile compact />
                  <button
                    className="navbar-toggler"
                    type="button"
                    style={{
                      padding: "0.25rem 0.5rem",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => setMenuOpen(!menuOpen)}
                  >
                    <span className="navbar-toggler-icon" />
                  </button>
                </div>
              </div>
            )}

            {!user && (
              <button
                className="navbar-toggler d-1200-none"
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <span className="navbar-toggler-icon" />
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div
            className="d-1200-none"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              padding: "1rem",
              position: "absolute",
              top: "100%",
              left: 0,
              width: "100%",
            }}
          >
            <NavLinks
              items={navItems}
              locationPathname={location.pathname}
              locationSearch={location.search}
              onNavigate={() => setMenuOpen(false)}
              containerRef={mobileNavRef}
              underlineRef={mobileUnderlineRef}
              underlineStyle={mobileUnderlineStyle}
              className="navbar-nav fw-bold text-center mb-3 position-relative"
              style={{
                paddingBottom: "2px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.5rem",
              }}
            />

            {!user && (
              <div className="text-center">
                <Button
                  text="Sign Up"
                  gradient="linear-gradient(135deg, #004e92, #00c6ff)"
                  onClick={() => {
                    setShowSignInModal(true);
                    setMenuOpen(false);
                  }}
                />
              </div>
            )}
          </div>
        )}
      </nav>

      <SignInModal
        open={showSignInModal}
        onClose={closeSignInModal}
        onGoogle={handleGoogleSignIn}
        onGitHub={handleGitHubSignIn}
      />

      <ProviderChooserModal
        open={showChooserModal}
        providers={chooserProviders}
        onClose={() => setShowChooserModal(false)}
        onSelect={handleChooserSelect}
      />

      <GdgocModal
        open={showGdgocModal}
        onCancel={() => setShowGdgocModal(false)}
        onProceed={() => {
          window.open(GDGOC_URL, "_blank");
          setShowGdgocModal(false);
        }}
      />

      {/* Custom breakpoints */}
      <style>
        {`\n          @media (min-width: 1200px) {\n            .d-1200-flex { display: flex !important; }\n            .d-1200-block { display: block !important; }\n            .d-1200-none { display: none !important; }\n          }\n          @media (max-width: 1199.98px) {\n            .d-1200-flex,\n            .d-1200-block { display: none !important; }\n            .d-1200-none { display: block !important; }\n          }\n        `}
      </style>
      <NavToast />
    </>
  );
};

export default Navbar;
